import { useState } from 'react';

import {
  ADD_ONS,
  LAUNCH_PROMO,
  PLANS,
  discountPct,
  monthlyPrice,
  type PlanTier,
} from '@dca/shared';

import { DataTable } from '../components/DataTable.tsx';
import { Badge, Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync, useAuth } from '../lib/auth.tsx';
import { money, number, shortDate } from '../lib/format.ts';

const INVOICE_TONE = {
  paid: 'good',
  open: 'muted',
  overdue: 'critical',
  void: 'muted',
} as const;

export function BillingPage() {
  const { me, refresh } = useAuth();
  const billing = useAsync(() => api.billing.subscription(), []);
  const usage = useAsync(() => api.billing.usage(), []);
  const [promo, setPromo] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<{
    amountFormatted: string;
    payTo: string;
    reference: string;
    steps: string[];
    note: string;
  } | null>(null);
  const [busy, setBusy] = useState<PlanTier | null>(null);
  // Add-on blocks being edited. `null` means "not touched yet", so the fields
  // follow the saved subscription until the subscriber actually changes one.
  const [extraShops, setExtraShops] = useState<number | null>(null);
  const [extraLocations, setExtraLocations] = useState<number | null>(null);

  const choose = async (tier: PlanTier) => {
    setBusy(tier);
    try {
      const result = await api.billing.subscribe(tier);
      setInstructions(result.instructions);
      await refresh();
      billing.reload();
      usage.reload();
    } finally {
      setBusy(null);
    }
  };

  const claimPromo = async () => {
    setBusy(LAUNCH_PROMO.tier);
    setPromoError(null);
    try {
      const result = await api.billing.claimPromo();
      setPromo(result.message);
      await refresh();
      billing.reload();
      usage.reload();
    } catch (error) {
      setPromoError(error instanceof Error ? error.message : 'Could not apply the promo.');
    } finally {
      setBusy(null);
    }
  };

  if (billing.loading) return <Spinner />;
  const current = me?.subscriber.tier ?? 'free';
  const subscription = billing.data?.subscription ?? null;
  const shops = extraShops ?? subscription?.extraChestshops ?? 0;
  const locations = extraLocations ?? subscription?.extraLocations ?? 0;
  const addOnsChanged =
    shops !== (subscription?.extraChestshops ?? 0) ||
    locations !== (subscription?.extraLocations ?? 0);

  const saveAddOns = async () => {
    setBusy(current);
    try {
      const result = await api.billing.subscribe(current, {
        extraChestshops: shops,
        extraLocations: locations,
      });
      setInstructions(result.instructions);
      setExtraShops(null);
      setExtraLocations(null);
      await refresh();
      billing.reload();
      usage.reload();
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Plan and billing</h1>
          <p>
            Billed in in-game currency. Choosing a plan raises an invoice; the plan activates when
            our firm account webhook confirms your transfer has landed.
          </p>
        </div>
        <Badge tone="muted">Current plan: {PLANS[current].name}</Badge>
      </header>

      {instructions && (
        <Card
          title="Pay this invoice in game"
          actions={
            <button className="ghost" onClick={() => setInstructions(null)}>
              Dismiss
            </button>
          }
        >
          <div className="stack">
            <div className="row" style={{ gap: 24 }}>
              <div>
                <div className="small muted">Amount</div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>{instructions.amountFormatted}</div>
              </div>
              <div>
                <div className="small muted">Pay to</div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>{instructions.payTo}</div>
              </div>
              <div>
                <div className="small muted">Memo reference</div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>{instructions.reference}</div>
              </div>
            </div>
            <ol className="muted small" style={{ paddingLeft: 20, margin: 0 }}>
              {instructions.steps.map((step) => (
                <li key={step} style={{ marginBottom: 4 }}>
                  {step}
                </li>
              ))}
            </ol>
            <Notice tone="warn">{instructions.note}</Notice>
          </div>
        </Card>
      )}

      {/* Usage against the plan. Tiers are sold on two numbers, so those are the
          two numbers the subscriber gets to see - not a vague "you are over". */}
      {usage.data && (
        <Card title="Your usage">
          <div className="usage-grid">
            <div>
              <div className="small muted">Chestshops</div>
              <div className="usage-figure">
                {number(usage.data.chestshopsUsed)}
                <span className="muted">
                  {' / '}
                  {usage.data.maxChestshops == null
                    ? 'unlimited'
                    : number(usage.data.maxChestshops)}
                </span>
              </div>
            </div>
            <div>
              <div className="small muted">Locations</div>
              <div className="usage-figure">
                {number(usage.data.locationsUsed)}
                <span className="muted">
                  {' / '}
                  {usage.data.maxLocations == null ? 'unlimited' : usage.data.maxLocations}
                </span>
              </div>
            </div>
            <div>
              <div className="small muted">Status</div>
              <div className="usage-figure">
                {usage.data.withinLimits ? (
                  <Badge tone="good">within plan</Badge>
                ) : (
                  <Badge tone="warning">over plan</Badge>
                )}
              </div>
            </div>
          </div>
          {!usage.data.withinLimits && (
            <Notice tone="warn">
              {usage.data.overChestshops > 0 &&
                `${number(usage.data.overChestshops)} chestshop${usage.data.overChestshops === 1 ? '' : 's'} beyond your plan. `}
              {usage.data.overLocations > 0 &&
                `${usage.data.overLocations} location${usage.data.overLocations === 1 ? '' : 's'} beyond your plan. `}
              Those are hidden from your dashboard until you upgrade or add blocks.
              {usage.data.suggestedTier &&
                ` ${PLANS[usage.data.suggestedTier].name} would cover everything you have.`}
            </Notice>
          )}
        </Card>
      )}

      {/* Launch promo. Only worth showing to someone who could actually take it. */}
      {current === 'free' && !promo && (
        <Notice tone="info">
          <div className="spread" style={{ gap: 12, flexWrap: 'wrap' }}>
            <span>{LAUNCH_PROMO.blurb}</span>
            <button
              className="primary"
              disabled={busy != null}
              onClick={claimPromo}
              style={{ whiteSpace: 'nowrap' }}
            >
              {busy === LAUNCH_PROMO.tier ? 'Working...' : 'Claim free month'}
            </button>
          </div>
        </Notice>
      )}
      {promo && <Notice tone="good">{promo}</Notice>}
      {promoError && <Notice tone="danger">{promoError}</Notice>}

      <div className="grid three">
        {Object.values(PLANS).map((plan) => (
          <div className="card" key={plan.tier}>
            <div className="spread" style={{ marginBottom: 8 }}>
              <h2>{plan.name}</h2>
              {plan.tier === current && <Badge tone="good">current</Badge>}
            </div>
            <div style={{ fontSize: 26, fontWeight: 600, marginBottom: 4 }}>
              {plan.price === 0 ? 'Free' : money(plan.price, 0)}
              {plan.price > 0 && <span className="small muted"> / {plan.cycleDays} days</span>}
            </div>
            {/* The same reduction the pricing page advertises, shown where the
                decision is actually made. */}
            {plan.listPrice != null && (
              <div className="tier-was" style={{ marginBottom: 6 }}>
                <s>{money(plan.listPrice, 0)}</s>
                <span className="tier-save">save {discountPct(plan)}%</span>
              </div>
            )}
            <p className="muted small">{plan.blurb}</p>
            <ul className="small muted" style={{ paddingLeft: 18, margin: '0 0 14px' }}>
              <li>
                {plan.benchmarkItemLimit == null
                  ? 'Every item you sell'
                  : `${plan.benchmarkItemLimit} items`}{' '}
                benchmarked
              </li>
              <li>
                {plan.maxChestshops == null
                  ? 'Unlimited chestshops'
                  : `Up to ${number(plan.maxChestshops)} chestshops`}
              </li>
              <li>
                {plan.maxLocations == null
                  ? 'Any number of locations'
                  : `${plan.maxLocations} location${plan.maxLocations === 1 ? '' : 's'}`}
              </li>
              <li>{plan.historyDays} days of history</li>
              <li>
                {plan.firmLimit} firm account{plan.firmLimit === 1 ? '' : 's'}, plus your personal
                account
              </li>
              {plan.features.includes('alerts.undercut') && <li>Undercut alerts</li>}
              {plan.features.includes('demand.heatmap') && <li>Demand heatmap</li>}
              {plan.features.includes('restock') && <li>Restock detection</li>}
              {plan.features.includes('opportunity') && <li>Opportunity finder</li>}
              {plan.features.includes('recommendations') && <li>Pricing recommendations</li>}
              {plan.features.includes('alerts.discord') && <li>Discord alerts</li>}
            </ul>
            <button
              className={plan.tier === current ? '' : 'primary'}
              disabled={plan.tier === current || busy != null}
              onClick={() => choose(plan.tier)}
            >
              {plan.tier === current
                ? 'Your plan'
                : busy === plan.tier
                  ? 'Working...'
                  : plan.price === 0
                    ? 'Switch to Free'
                    : `Choose ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      {/* Add-ons. Between Wood at 50 signs and Iron at 250 there is a lot of
          room, and an owner sitting at 70 should not have to buy the tier above
          to see the last 20. */}
      {current !== 'free' && (
        <Card
          title="Add-ons"
          subtitle="Top up your current plan instead of moving a whole tier."
        >
          <div className="usage-grid">
            <label>
              <div className="small muted">
                Extra chestshops ({money(ADD_ONS.extraChestshops.price, 0)} per block of{' '}
                {ADD_ONS.extraChestshops.blockSize})
              </div>
              <input
                type="number"
                min={0}
                step={ADD_ONS.extraChestshops.blockSize}
                value={shops}
                onChange={(event) => setExtraShops(Math.max(0, Number(event.target.value) || 0))}
              />
            </label>
            <label>
              <div className="small muted">
                Extra locations ({money(ADD_ONS.extraLocation.price, 0)} each)
              </div>
              <input
                type="number"
                min={0}
                step={1}
                value={locations}
                onChange={(event) => setExtraLocations(Math.max(0, Number(event.target.value) || 0))}
              />
            </label>
            <div>
              <div className="small muted">New monthly total</div>
              <div className="usage-figure">{money(monthlyPrice(current, shops, locations), 0)}</div>
            </div>
          </div>
          <button className="primary" disabled={busy != null || !addOnsChanged} onClick={saveAddOns}>
            {busy != null ? 'Working...' : addOnsChanged ? 'Update add-ons' : 'No changes'}
          </button>
        </Card>
      )}

      <Card
        title="Invoices"
        subtitle={
          billing.data ? `Payments go to the firm account ${billing.data.billingAccount}` : undefined
        }
      >
        {!subscription || subscription.invoices.length === 0 ? (
          <Empty title="No invoices yet" />
        ) : (
          <DataTable
            rows={subscription.invoices}
            rowKey={(invoice) => invoice.id}
            initialSort={{ key: 'due', dir: 'desc' }}
            searchPlaceholder="Search reference"
            columns={[
              {
                key: 'reference',
                header: 'Reference',
                searchText: (invoice) => invoice.reference,
                sortValue: (invoice) => invoice.reference,
                render: (invoice) => <code>{invoice.reference}</code>,
              },
              {
                key: 'amount',
                header: 'Amount',
                align: 'num',
                sortValue: (invoice) => Number(invoice.amount),
                render: (invoice) => money(Number(invoice.amount), 0),
              },
              {
                key: 'status',
                header: 'Status',
                sortValue: (invoice) => invoice.status,
                render: (invoice) => (
                  <Badge tone={INVOICE_TONE[invoice.status]}>{invoice.status}</Badge>
                ),
              },
              {
                key: 'due',
                header: 'Due',
                align: 'num',
                sortValue: (invoice) => invoice.dueAt,
                render: (invoice) => shortDate(invoice.dueAt),
              },
              {
                key: 'paid',
                header: 'Paid',
                align: 'num',
                sortValue: (invoice) => invoice.paidAt,
                render: (invoice) => (invoice.paidAt ? shortDate(invoice.paidAt) : '-'),
              },
            ]}
          />
        )}
      </Card>
    </>
  );
}
